import { useState, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import './Monitoring.css';
import Menu from '../Shared/Menu';
import usePageTitle from '../Shared/usePageTitle';
import {
    JOURNEY_HEALTH_ROWS,
    JOURNEY_BADGE_CLASS,
} from '../../data/monitoringData';

const FILTERS = ['All', 'Active', 'Paused', 'Draft', 'Stopped'];

const KPIS = [
    {
        health: 'ActiveInFlight', cardClass: 'jh-kpi-active', iconClass: 'jh-icon-active', valueClass: 'jh-kpi-value-active',
        label: 'Active Journeys', subtext: 'active with contacts in-flight',
        icon: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="M22 4L12 14.01l-3-3" /></>,
    },
    {
        health: 'ZeroContacts', cardClass: 'jh-kpi-zero', iconClass: 'jh-icon-zero', valueClass: 'jh-kpi-value-zero',
        label: 'Zero Contacts', subtext: 'active with 0 contacts in-flight',
        icon: <><circle cx="12" cy="8" r="4" /><path d="M4 20a8 8 0 0 1 16 0" /></>,
    },
    {
        health: 'GoalNotMet', cardClass: 'jh-kpi-goal', iconClass: 'jh-icon-goal', valueClass: 'jh-kpi-value-goal',
        label: 'Goal Not Met', subtext: 'goal completion below 10%',
        icon: <><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></>,
    },
    {
        health: 'Unpublished', cardClass: 'jh-kpi-draft', iconClass: 'jh-icon-draft', valueClass: 'jh-kpi-value-draft',
        label: 'Unpublished', subtext: 'draft, never published',
        icon: <><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></>,
    },
];

function JourneyHealth() {
    usePageTitle('Journey Health');

    const [query, setQuery]       = useState('');
    const [filter, setFilter]     = useState('All');   // status filter or health key
    const [selected, setSelected] = useState(null);

    const healthCounts = useMemo(() => {
        const tally = {};
        JOURNEY_HEALTH_ROWS.forEach(row => {
            row.health.forEach(h => { tally[h] = (tally[h] || 0) + 1; });
        });
        return tally;
    }, []);

    const visibleRows = useMemo(() => {
        const q = query.trim().toLowerCase();
        return JOURNEY_HEALTH_ROWS.filter(row => {
            if (filter !== 'All') {
                const matches = FILTERS.includes(filter)
                    ? row.status === filter
                    : row.health.includes(filter);
                if (!matches) return false;
            }
            if (!q) return true;
            return row.name.toLowerCase().includes(q)
                || row.folder.toLowerCase().includes(q)
                || row.entry.toLowerCase().includes(q);
        });
    }, [query, filter]);

    const toggleKpi = value => setFilter(current => (current === value ? 'All' : value));

    return (
        <>
            <Menu />

            <div className="container fade-up container-main">
                <div className="page-header mb-4">
                    <h1>Journey Health</h1>
                    <p>Live status across every journey — in-flight contacts, empty actives, missed goals, and unpublished drafts</p>
                </div>

                <div className="row g-3 mb-4">
                    {KPIS.map(kpi => (
                        <div className="col-6 col-md-3" key={kpi.health}>
                            <button
                                type="button"
                                className={`kpi-card ah-kpi-card ${kpi.cardClass} d-flex align-items-start gap-3${filter === kpi.health ? ' active' : ''}`}
                                onClick={() => toggleKpi(kpi.health)}
                            >
                                <div className={`kpi-icon ${kpi.iconClass}`}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                         stroke="currentColor" strokeWidth="2">
                                        {kpi.icon}
                                    </svg>
                                </div>
                                <div>
                                    <p className="kpi-label">{kpi.label}</p>
                                    <p className={`kpi-value ${kpi.valueClass}`}>{healthCounts[kpi.health] || 0}</p>
                                    <p className="kpi-subtext">{kpi.subtext}</p>
                                </div>
                            </button>
                        </div>
                    ))}
                </div>

                <div className="d-flex flex-column flex-lg-row gap-3 mb-3 align-items-stretch align-items-lg-center">
                    <div className="search-wrapper">
                        <svg className="search-icon" width="15" height="15" viewBox="0 0 24 24"
                             fill="none" stroke="#94A3B8" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.35-4.35" />
                        </svg>
                        <input
                            type="text"
                            className="search-input"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            placeholder="Search journeys, folders or entry sources…"
                        />
                    </div>
                    <div className="filter-buttons ah-filters d-flex flex-wrap gap-1">
                        {FILTERS.map(f => (
                            <button
                                key={f}
                                type="button"
                                className={`filter-btn${filter === f ? ' active' : ''}`}
                                onClick={() => setFilter(f)}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                </div>

                <p className="de-results-count mb-3">
                    Showing <strong>{visibleRows.length}</strong> of {JOURNEY_HEALTH_ROWS.length} journeys
                </p>

                <div className="d-flex flex-column gap-3">
                    {visibleRows.length === 0 ? (
                        <p className="xbu-empty">No journeys match the current filters.</p>
                    ) : visibleRows.map(row => (
                        <button
                            type="button"
                            key={row.name}
                            className="email-row-card ah-row jh-row"
                            onClick={() => setSelected(row)}
                        >
                            <div className="email-accent-stripe" style={{ background: row.stripe }} />
                            <div className="email-card-content">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                            <h3 className="email-card-title">{row.name}</h3>
                                            <span className={`ah-badge ${JOURNEY_BADGE_CLASS[row.status]}`}>
                                                <span className="ah-badge-dot" />{row.status}
                                            </span>
                                        </div>
                                        <p className="email-meta-text mb-0">{row.folder} · {row.entry}</p>
                                    </div>
                                    <span className="ah-chevron" aria-hidden="true">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                             stroke="currentColor" strokeWidth="2.5">
                                            <path d="M9 6l6 6-6 6" />
                                        </svg>
                                    </span>
                                </div>
                                <div className="email-metrics-row">
                                    <div className="email-metric-item">
                                        <p className="email-metric-label">Active contacts</p>
                                        <p className="email-metric-value">{row.contacts}</p>
                                    </div>
                                    <div className="email-metric-item">
                                        <p className="email-metric-label">Goal</p>
                                        <p className="email-metric-value" style={row.goalColor ? { color: row.goalColor } : undefined}>
                                            {row.goal}
                                        </p>
                                    </div>
                                    <div className="email-metric-item">
                                        <p className="email-metric-label">Last published</p>
                                        <p className="email-metric-value">{row.published}</p>
                                    </div>
                                    <div className="email-metric-item">
                                        <p className="email-metric-label">Entry source</p>
                                        <p className="email-metric-value">{row.entry}</p>
                                    </div>
                                    <div className="email-metric-item">
                                        <p className="email-metric-label">Folder path</p>
                                        <p className="email-metric-value">{row.folder}</p>
                                    </div>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Detail panel */}
            <div className={`ah-panel-backdrop${selected ? ' show' : ''}`} onClick={() => setSelected(null)} />
            <div className={`ah-panel-slide${selected ? ' show' : ''}`} data-ah-status={selected ? selected.status : ''}>
                {selected && (
                    <>
                        <div className="ah-panel-hero">
                            <button type="button" className="ah-panel-close"
                                    onClick={() => setSelected(null)} aria-label="Close">✕</button>
                            <div className="d-flex align-items-center gap-3">
                                <div className="ah-panel-hero-icon">
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                         stroke="currentColor" strokeWidth="2">
                                        <circle cx="5" cy="6" r="2" />
                                        <circle cx="19" cy="18" r="2" />
                                        <path d="M7 6h6a4 4 0 0 1 4 4v6" />
                                    </svg>
                                </div>
                                <div className="ah-panel-hero-copy">
                                    <span className={`ah-badge ${JOURNEY_BADGE_CLASS[selected.status]} mb-2`}>
                                        <span className="ah-badge-dot" />{selected.status}
                                    </span>
                                    <h2 className="ah-panel-title">{selected.name}</h2>
                                    <p className="ah-panel-hero-sub">{selected.folder}</p>
                                </div>
                            </div>
                        </div>

                        <div className="ah-panel-body">
                            <p className="ah-panel-section">Journey snapshot</p>
                            <div className="ah-panel-metrics mb-4">
                                <div className="ah-panel-metric">
                                    <div className="ah-panel-metric-icon" style={{ background: '#6366F114', color: '#4F46E5' }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="8" r="4" /><path d="M4 20a8 8 0 0 1 16 0" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="ah-panel-metric-label">Active contacts</p>
                                        <p className="ah-panel-metric-value">{selected.contacts}</p>
                                    </div>
                                </div>
                                <div className="ah-panel-metric">
                                    <div className="ah-panel-metric-icon" style={{ background: '#10B98114', color: '#059669' }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="ah-panel-metric-label">Goal completion</p>
                                        <p className="ah-panel-metric-value">{selected.goal}</p>
                                    </div>
                                </div>
                                <div className="ah-panel-metric">
                                    <div className="ah-panel-metric-icon" style={{ background: '#0EA5E914', color: '#0284C7' }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="3" y="4" width="18" height="18" rx="2" />
                                            <path d="M16 2v4M8 2v4M3 10h18" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="ah-panel-metric-label">Last published</p>
                                        <p className="ah-panel-metric-value">{selected.published}</p>
                                    </div>
                                </div>
                                <div className="ah-panel-metric">
                                    <div className="ah-panel-metric-icon" style={{ background: '#8B5CF614', color: '#7C3AED' }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M4 4h16v6H4zM4 14h16v6H4z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="ah-panel-metric-label">Entry source</p>
                                        <p className="ah-panel-metric-value">{selected.entry}</p>
                                    </div>
                                </div>
                            </div>

                            <p className="ah-panel-section">Details</p>
                            <div className="ah-panel-details mb-4">
                                <div className="ah-panel-detail-row">
                                    <span className="ah-panel-detail-label">Status</span>
                                    <span className="ah-panel-detail-value">{selected.status}</span>
                                </div>
                                <div className="ah-panel-detail-row">
                                    <span className="ah-panel-detail-label">Folder path</span>
                                    <span className="ah-panel-detail-value">{selected.folder}</span>
                                </div>
                            </div>
                        </div>

                        <div className="ah-panel-footer">
                            <NavLink className="ah-open-btn" to="/journeys">
                                Open in Journeys
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                                     stroke="currentColor" strokeWidth="2.5">
                                    <path d="M9 6l6 6-6 6" />
                                </svg>
                            </NavLink>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}

export default JourneyHealth;
